<!--
  OmniCheckboxes Component

  Description:
    This component renders a list of checkboxes, optionally with draggable functionality.
    It is designed to be used as a filter selector where options can be displayed in a customizable order, 
    which can be altered through drag-and-drop if enabled. The component integrates with BootstrapVue 
    for styling the checkboxes and vue-draggable for the drag-and-drop feature (if enabled).

  Usage:
    <OmniCheckboxes :options="options" v-model="pickedOptions" :draggable="true" />

  Props:
    - options (Array): An array of objects, each representing a checkbox. Each object should have 
      properties such as 'text' (label of the checkbox), 'value' (value of the checkbox), 
      and 'disabled' (state of the checkbox).
    - value (Array): Used with v-model to sync the selected options. It reflects the values of the picked options.
    - draggable (Boolean): Optional. If set to true, enables drag-and-drop functionality for reordering checkboxes.

  Example of 'options' Prop Structure:
    [
      { text: "Ordernummer", value: "salesOrder_identifier" },
      { text: "Klantnummer", value: "customer_identifier" },
      ...
    ]

  Example of 'value' (v-model) Structure:
    [
      "created_at",
      "salesOrder_identifier"
    ]
  i.e. it accepts and returns the value properties of selected options, in their respective order.

  Note:
    - The 'options' prop is an array where each element is an object with properties like 'text', 'value', and 'disabled'.
    - The 'v-model' binds to an array that contains the values of selected options, e.g., ['salesOrder_identifier', 'customer_identifier'].
    - The 'draggable' prop is optional. When set to true, the checkboxes can be reordered through drag-and-drop.
    - The component uses BootstrapVue for the styling of checkboxes and vue-draggable for enabling drag-and-drop functionality (if draggable is true).
-->

<template>
    <Draggable
        v-model="internalOptions"
        :options="{ animation: 150 }"
        class="omnistock omni-checkboxes"
        :class="{grid: grid, 'no-grid': !grid}"
        v-if="draggable"
        @end="emitNewValues"
        :group="randomGroupId"
        :disabled="disabled"
    >
        <div
            v-for="(option, index) in internalOptions"
            :key="option.value"
            class="checkbox-container draggable"
        >
            <FontAwesomeIcon icon="grip-lines-vertical" class="grip-icon" />
            <BFormCheckbox
                v-if="!hideCheckboxes"
                v-model="internalPicked"
                :value="option.value"
                :disabled="option.disabled || disabled"
            >
                <template v-if="prependNumbers">
                    {{ index + 1}}.&nbsp;
                </template>

                <span v-html="option.text"/>
            </BFormCheckbox>

            <span v-else>
                <template v-if="prependNumbers">
                    {{ index + 1}}.&nbsp;
                </template>

                <span v-html="option.text"/>
            </span>
        </div>
    </Draggable>

    <div v-else class="omnistock omni-checkboxes">
        <div
            v-for="option in options"
            :key="option.value"
            class="checkbox-container"
        >
            <BFormCheckbox
                v-if="!hideCheckboxes"
                v-model="internalPicked"
                :value="option.value"
                :disabled="option.disabled || disabled"
            >
                <span v-html="option.text"/>
            </BFormCheckbox>

            <span v-else v-html="option.text"/>
        </div>
    </div>
</template>

<script>
import Draggable from "vuedraggable";
import { isEqual } from "lodash";

export default {
    name: "OmniCheckboxes",
    components: { Draggable },
    props: {
        draggable: {
            type: Boolean,
            required: false,
            default: false
        },
        options: {
            type: Array,
            required: true,
        },
        value: {
            required: true,
        },
        hideCheckboxes: {
            type: Boolean,
            required: false,
            default: false,
        },
        prependNumbers: {
            type: Boolean,
            required: false,
            default: false,
        },
        grid: {
            type: Boolean,
            required: false,
            default: true,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    computed: {
        /**
         * Filters internalOptions to include only those that are selected in internalPicked,
         * maintaining the (draggable) order of internalOptions.
         */
        internalPickedWithOrder() {
            return this.internalOptions.filter(option => this.internalPicked.includes(option.value));
        },

        /**
         * Maps the filtered, ordered options to their values. I.e. the values as we want to emit to the parent.
         */
        internalPickedWithOrderAsArray() {
            return this.internalPickedWithOrder.map(option => option.value);
        }
    },
    data() {
        return {
            internalOptions: [],
            internalPicked: this.value,
            randomGroupId: Date.now(),
        };
    },
    mounted() {
        this.internalOptions = this.options
        this.sortOptions()
    },

    watch: {
        // Watch for newly picked values.
        internalPicked() {
            this.emitNewValues();
        },

        // Allow the parent to set values via v-model, but avoid getting stuck in a loop.
        value(newVal, oldVal) {
            if (!isEqual(newVal, oldVal)) {
                this.sortOptions()
                this.internalPicked = newVal
            }
        },
    },
    methods: {
        emitNewValues() {
            this.$emit("input", this.internalPickedWithOrderAsArray);
        },
        sortOptions() {
            if (!this.draggable) {
                return;
            }

            this.internalOptions = this.options.sort((a, b) => {
                let aIndex = this.value?.indexOf(a.value) ?? -1;
                let bIndex = this.value?.indexOf(b.value) ?? -1;

                if (aIndex !== -1 && bIndex !== -1) {
                    return aIndex - bIndex;
                }
                if (aIndex !== -1 && bIndex === -1) {
                    return -1
                }
                if (aIndex === -1 && bIndex !== -1) {
                    return 1
                }

                // If neither element is in the value array, keep their original order
                return 0;
            });
        }
    }
};
</script>

<style lang="scss">
.omnistock.omni-checkboxes.grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);

    .checkbox-container {
        display: flex;
        column-gap: .75rem;
        align-items: center;
        &.draggable {
            cursor: grab;
        }

        div.custom-checkbox {
            display: inline-block;
        }
    }

    &.list {
        grid-template-columns: repeat(1, 1fr);
    }
}

.omnistock.omni-checkboxes.no-grid {
    .checkbox-container {
        display:inline-block;
        margin-right: 40px;
        margin-bottom: 10px;

        &.draggable {
            cursor: grab;
        }

        div.custom-checkbox {
            margin-left: 5px;
            display: inline-block;
        }
    }
}
</style>
